import {
  formatCurrency,
  ORDER_DISPLAY_ITEM_PRICES_FRAGMENT,
  ORDER_FEES_DETAILS_FRAGMENT,
  ORDER_FEES_FIELDS_FRAGMENT,
} from '@/utils/money';
import { gql } from '@apollo/client';
import { ORDER_ETA_FRAGMENT } from '@/components/orders/order-eta-banner';
import { ORDER_FULFILLMENT_DATE_FRAGMENT } from '@/components/orders/order-fulfillment-date';
import {
  ORDER_ITEM_LIST_FRAGMENT,
  ORDER_ITEM_RATING_FRAGMENT,
} from '@/components/order-item-list';
import { ORDER_STATUS_LABEL_FIELDS } from '@/components/orders/order-status-label';
import {
  OrderFeeDisplayType,
  Orders_GetOrderQuery,
  Orders_GetOrderQueryVariables,
  Orders_GetOrderSummaryQuery,
  Orders_GetOrderSummaryQueryVariables,
} from '@/types';
import { useGetDynamicConfigValueForChat } from '@/hooks/use-dynamic-config-value';
import { useItemQueryLocale } from '@/hooks/use-item-query-variables';
import { useSupplierCurrencyCode } from '@/hooks/use-supplier-currency-code';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

export const ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT = gql`
  fragment OrderInvoiceCheckInFields on order_invoice_check_ins {
    completed_at
    uuid
  }
`;

export const ORDER_PLACEMENT_FIELDS_FRAGMENT = gql`
  fragment OrderPlacementFields on orders {
    placed_at
    placed_by {
      name
      supplier_employee_overrides(
        where: { supplier: { supplier_orders: { uuid: { _eq: $orderUUID } } } }
      ) {
        name
        uuid
      }
      uuid
    }
    storefront_metadata {
      name
      uuid
    }
  }
`;

export const ORDER_ITEM_FIELDS_FRAGMENT = gql`
  fragment GetOrderItemFields on orders_items {
    ...OrderItemListFields
    order_item_rating {
      ...OrderItemRatingFields
    }
  }
  ${ORDER_ITEM_LIST_FRAGMENT}
  ${ORDER_ITEM_RATING_FRAGMENT}
`;

export const ORDER_INVOICE_LINE_ITEM_FRAGMENT = gql`
  fragment OrderInvoiceLineItemFields on order_invoice_line_items {
    item_code
    item_name
    ship_erp_unit_of_measure
    ship_quantity
    unit_price_in_micros
    unit_tax_in_micros
    uuid
    variant_pack_uuid
  }
`;

export const ORDER_INVOICE_ITEMS_FIELDS_FRAGMENT = gql`
  fragment OrderInvoiceItemsFields on order_invoices {
    order_invoice_line_items {
      ...OrderInvoiceLineItemFields
    }
  }

  ${ORDER_INVOICE_LINE_ITEM_FRAGMENT}
`;

export const ORDER_INVOICE_PARTIAL_PAYMENT_FRAGMENT = gql`
  fragment OrderInvoicePartialPaymentFields on order_invoices {
    payment_batch_line_items {
      amount_in_micros
      payment_batch {
        status
        uuid
      }
      uuid
    }
  }
`;

const GET_ORDER_INVOICE_BY_ORDER_FIELDS_FRAGMENT = gql`
  fragment Orders_GetOrderInvoiceByOrderFields on OrderInvoiceByOrderResponse {
    order_invoice {
      balance_in_micros
      document_url
      order_invoice_check_in {
        ...OrderInvoiceCheckInFields
      }
      due_at
      erp_balance_in_micros
      invoice_number
      order_invoice_line_items {
        ...OrderInvoiceLineItemFields
      }
      order_invoice_status {
        balance_in_micros
        use_pepper_balance
        uuid
      }
      ...OrderInvoicePartialPaymentFields
      seen
      uuid
    }
    public_url
  }

  ${ORDER_INVOICE_CHECK_IN_FIELDS_FRAGMENT}
  ${ORDER_INVOICE_LINE_ITEM_FRAGMENT}
  ${ORDER_INVOICE_PARTIAL_PAYMENT_FRAGMENT}
`;

export const GET_ORDER_QUERY = gql`
  fragment Orders_GetOrderFields on orders {
    ...OrderDisplayItemPricesFields
    ...OrderStatusLabelFields
    ...OrderETAFields
    ...OrderPlacementFields
    ...OrderFulfillmentDateFields
    additional_input_values
    currency_code
    customer_facing_id
    notes
    orders_items(order_by: { added_at: asc }) {
      ...GetOrderItemFields
    }
    parent_order_uuid
    po_number
    restaurant_desired_delivery_time
    smart_orders(limit: 1) {
      uuid
      merge_uuid
      thread_id
    }
    parent_smart_orders(limit: 1) {
      uuid
      merge_uuid
      thread_id
    }
    supplier {
      supplier_metadata {
        display_item_code
        gtin_settings
        uuid
      }
      name
      uuid
    }
    supplier_accepted_delivery_time
    uuid
  }

  query Orders_GetOrder(
    $locale: String!
    $orderUUID: uuid!
    $restaurantUUID: uuid!
    $skipSaltApi: Boolean!
    $supplierUUID: uuid!
  ) {
    getOrderInvoiceByOrder(order_id: $orderUUID) {
      ...Orders_GetOrderInvoiceByOrderFields
    }

    getOrder(
      order_id: $orderUUID
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      order {
        ...Orders_GetOrderFields
      }
      translated_fulfillment_display_name
    }

    getOrderFees(order_ids: [$orderUUID], supplier_id: $supplierUUID) {
      ...OrderFeesDetailsFields
    }
  }

  ${ORDER_ETA_FRAGMENT}
  ${ORDER_DISPLAY_ITEM_PRICES_FRAGMENT}
  ${ORDER_FEES_FIELDS_FRAGMENT}
  ${ORDER_ITEM_FIELDS_FRAGMENT}
  ${ORDER_PLACEMENT_FIELDS_FRAGMENT}
  ${ORDER_STATUS_LABEL_FIELDS}
  ${GET_ORDER_INVOICE_BY_ORDER_FIELDS_FRAGMENT}
  ${ORDER_FULFILLMENT_DATE_FRAGMENT}
  ${ORDER_FEES_DETAILS_FRAGMENT}
`;

export const GET_ORDER_SUMMARY_QUERY = gql`
  query Orders_GetOrderSummary($orderUUID: uuid!) {
    getOrderGrossProfit(order_id: $orderUUID) {
      gross_profit {
        percentage
        amount_in_micros
        currency_code
      }
    }
  }
`;

/**
 * Retrieve an order for a specific restaurant
 *
 * @param orderUUID - The UUID of the order
 * @param chatUUID - The UUID of the chat associated with the order
 * @param restaurantUUID - The UUID of the restaurant to retrieve the order for
 * @param supplierUUID - The UUID of the supplier of the restaurant
 *
 * @returns The order requested, order fees, order invoice (if there is one) and the order summary
 */
export const useGetOrderForRestaurant = (
  orderUUID: string,
  chatUUID: string | undefined,
  restaurantUUID: string,
  supplierUUID: string,
) => {
  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { currencyCode: supplierCurrencyCode } =
    useSupplierCurrencyCode(supplierUUID);

  const itemQueryVariables = {
    locale: useItemQueryLocale(),
    skipSaltApi:
      !chatUUID ||
      !getDynamicConfigValueForChat(
        chatUUID,
        'salt_api_integration_enabled',
        false,
      ),
  };

  const { data, loading, hasResponse, refetch } = useWrappedQuery<
    Orders_GetOrderQuery,
    Orders_GetOrderQueryVariables
  >(GET_ORDER_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      ...itemQueryVariables,
      orderUUID,
      restaurantUUID,
      supplierUUID,
    },
  });

  const order = data?.getOrder.order;

  const orderFees = data?.getOrderFees;
  const totalOrderFee = orderFees?.find(
    (fee) => fee.display_type === OrderFeeDisplayType.Total,
  );

  const currencyCode = order?.currency_code ?? supplierCurrencyCode;
  const total = formatCurrency(
    totalOrderFee?.amount_at_order_micros ?? 0,
    currencyCode,
  );

  const unitCount = order?.orders_items.reduce(
    (result, oi) =>
      result + (oi.order_item_prices?.pack_quantity_at_order ?? 0),
    0,
  );

  return {
    currencyCode,
    data,
    hasResponse,
    loading,
    order,
    refetch,
    summary: {
      total: total ?? null,
      unitCount: unitCount ?? null,
    },
  };
};

export const useGetOrderSummary = (orderUUID: string) => {
  const { data, loading, error } = useWrappedQuery<
    Orders_GetOrderSummaryQuery,
    Orders_GetOrderSummaryQueryVariables
  >(GET_ORDER_SUMMARY_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: {
      orderUUID,
    },
  });

  return {
    error,
    grossProfit: data?.getOrderGrossProfit?.gross_profit,
    loading,
  };
};
